import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TenantService } from '../../../core/tenant/tenant.service';
import type {
  MyTaskItem,
  MyTasksDueStateFilter,
  MyTasksFilterOption,
  MyTasksStatusFilter,
  MyTasksSummary,
} from '../models/my-tasks.models';
import { MyTasksApiService } from '../services/my-tasks-api.service';

@Injectable({ providedIn: 'root' })
export class MyTasksStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(MyTasksApiService);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly itemsSignal = signal<MyTaskItem[]>([]);
  private readonly summarySignal = signal<MyTasksSummary>({
    openCount: 0,
    completedCount: 0,
    overdueCount: 0,
    dueSoonCount: 0,
  });
  private readonly workflowOptionsSignal = signal<MyTasksFilterOption[]>([]);
  private readonly projectOptionsSignal = signal<MyTasksFilterOption[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadingSummarySignal = signal(false);
  private readonly loadingMoreSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly pageSignal = signal(1);
  private readonly pageSizeSignal = signal(20);
  private readonly totalCountSignal = signal(0);
  private readonly selectedStatusFilterSignal = signal<MyTasksStatusFilter>('all');
  private readonly selectedDueStateFilterSignal = signal<MyTasksDueStateFilter>('all');
  private readonly selectedWorkflowIdSignal = signal<string | null>(null);
  private readonly selectedProjectIdSignal = signal<string | null>(null);
  private readonly searchTermSignal = signal('');

  readonly items = this.itemsSignal.asReadonly();
  readonly summary = this.summarySignal.asReadonly();
  readonly workflowOptions = this.workflowOptionsSignal.asReadonly();
  readonly projectOptions = this.projectOptionsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingSummary = this.loadingSummarySignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly page = this.pageSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly hasMore = computed(() => this.items().length < this.totalCount());
  readonly selectedStatusFilter = this.selectedStatusFilterSignal.asReadonly();
  readonly selectedDueStateFilter = this.selectedDueStateFilterSignal.asReadonly();
  readonly selectedWorkflowId = this.selectedWorkflowIdSignal.asReadonly();
  readonly selectedProjectId = this.selectedProjectIdSignal.asReadonly();
  readonly searchTerm = this.searchTermSignal.asReadonly();

  readonly isEmpty = computed(() => !this.loading() && !this.error() && this.items().length === 0);
  readonly hasItems = computed(() => this.items().length > 0);
  readonly overdueCount = computed(() =>
    this.summary().overdueCount || this.items().filter((item) => item.isOverdue).length
  );
  readonly dueSoonCount = computed(() =>
    this.summary().dueSoonCount || this.items().filter((item) => item.isDueSoon).length
  );

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }

      if (this.loadedCompanyIdSignal() !== companyId) {
        this.loadedCompanyIdSignal.set(companyId);
        this.reset();
      }
    });
  }

  async loadTasks(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.pageSignal.set(1);
    try {
      const result = await this.api.getMyTasks({
        page: 1,
        pageSize: this.pageSize(),
        status: this.selectedStatusFilter(),
        dueState: this.selectedDueStateFilter(),
        workflowId: this.selectedWorkflowId(),
        projectId: this.selectedProjectId(),
        search: this.searchTerm(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;

      this.itemsSignal.set(result.items);
      this.totalCountSignal.set(result.totalCount);
      this.workflowOptionsSignal.set(result.workflowOptions ?? []);
      this.projectOptionsSignal.set(result.projectOptions ?? []);
      if (result.summary) {
        this.summarySignal.set(result.summary);
      }
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.set([]);
      this.totalCountSignal.set(0);
      this.errorSignal.set('myTasks.states.error');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingSignal.set(false);
    }
  }

  async reloadTasks(): Promise<void> {
    await Promise.all([this.loadTasks(), this.loadSummary()]);
  }

  async loadSummary(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSummarySignal.set(true);
    try {
      const summary = await this.api.getSummary({
        status: this.selectedStatusFilter(),
        dueState: this.selectedDueStateFilter(),
        workflowId: this.selectedWorkflowId(),
        projectId: this.selectedProjectId(),
        search: this.searchTerm(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.summarySignal.set(summary);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingSummarySignal.set(false);
    }
  }

  async changeStatusFilter(value: MyTasksStatusFilter): Promise<void> {
    if (this.selectedStatusFilter() === value) return;
    this.selectedStatusFilterSignal.set(value);
    await this.reloadTasks();
  }

  async changeDueStateFilter(value: MyTasksDueStateFilter): Promise<void> {
    if (this.selectedDueStateFilter() === value) return;
    this.selectedDueStateFilterSignal.set(value);
    await this.reloadTasks();
  }

  async changeWorkflowFilter(value: string | null): Promise<void> {
    if (this.selectedWorkflowId() === value) return;
    this.selectedWorkflowIdSignal.set(value);
    await this.reloadTasks();
  }

  async changeProjectFilter(value: string | null): Promise<void> {
    if (this.selectedProjectId() === value) return;
    this.selectedProjectIdSignal.set(value);
    if (!value && this.selectedWorkflowId()) {
      this.selectedWorkflowIdSignal.set(null);
    }
    await this.reloadTasks();
  }

  async changeSearch(value: string): Promise<void> {
    if (this.searchTerm() === value) return;
    this.searchTermSignal.set(value);
    await this.reloadTasks();
  }

  async loadNextPage(): Promise<void> {
    if (!this.hasMore() || this.loadingMore()) return;
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingMoreSignal.set(true);
    const nextPage = this.page() + 1;
    try {
      const result = await this.api.getMyTasks({
        page: nextPage,
        pageSize: this.pageSize(),
        status: this.selectedStatusFilter(),
        dueState: this.selectedDueStateFilter(),
        workflowId: this.selectedWorkflowId(),
        projectId: this.selectedProjectId(),
        search: this.searchTerm(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.update((current) => [...current, ...result.items]);
      this.pageSignal.set(nextPage);
      this.totalCountSignal.set(result.totalCount);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set('myTasks.states.errorLoadMore');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingMoreSignal.set(false);
    }
  }

  reset(): void {
    this.itemsSignal.set([]);
    this.summarySignal.set({
      openCount: 0,
      completedCount: 0,
      overdueCount: 0,
      dueSoonCount: 0,
    });
    this.workflowOptionsSignal.set([]);
    this.projectOptionsSignal.set([]);
    this.loadingSignal.set(false);
    this.loadingSummarySignal.set(false);
    this.loadingMoreSignal.set(false);
    this.errorSignal.set(null);
    this.pageSignal.set(1);
    this.pageSizeSignal.set(20);
    this.totalCountSignal.set(0);
    this.selectedStatusFilterSignal.set('all');
    this.selectedDueStateFilterSignal.set('all');
    this.selectedWorkflowIdSignal.set(null);
    this.selectedProjectIdSignal.set(null);
    this.searchTermSignal.set('');
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }
}
